// Chat: the conversations list (Chats segment of the Friends tab) and the
// message thread sheet. Threads are keyed by member set server-side — a DM
// with the same friend, or a group with the same crew, is always one thread;
// plans with that crew drop system lines into it.

import React from 'react';
import { HeroArt, Icon, FriendAvatar } from './components.jsx';
import {
  larkleGetUid,
  apiGetConversation, apiFetchMessages, apiSendMessage, apiMarkConversationRead,
  apiStartConversation,
  placeToActivity,
} from './lib/api.js';

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Display name for a thread: explicit title, else the other members' first names.
export function conversationLabel(conversation, uid) {
  if (conversation.title) return conversation.title;
  const others = (conversation.members || []).filter(m => m.userId !== uid);
  if (others.length === 0) return 'Chat';
  const firsts = others.map(m => (m.name || m.handle || '?').split(' ')[0]);
  return firsts.length <= 3 ? firsts.join(', ') : `${firsts.slice(0, 3).join(', ')} +${firsts.length - 3}`;
}

function ThreadAvatar({ conversation, uid, size = 44 }) {
  const others = (conversation.members || []).filter(m => m.userId !== uid);
  if (others.length <= 1) {
    const one = others[0] || { userId: conversation.id, name: '?' };
    return <FriendAvatar friend={one} size={size}/>;
  }
  // Groups: two overlapping members
  const s = Math.round(size * 0.72);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div style={{ position: 'absolute', left: 0, top: 0 }}>
        <FriendAvatar friend={others[0]} size={s}/>
      </div>
      <div style={{ position: 'absolute', right: 0, bottom: 0, borderRadius: 999, boxShadow: '0 0 0 2px var(--bg)' }}>
        <FriendAvatar friend={others[1]} size={s}/>
      </div>
    </div>
  );
}

function previewText(conversation, uid) {
  const last = conversation.lastMessage;
  if (!last) return 'Say hi';
  const who = last.senderId === uid
    ? 'You'
    : ((conversation.members || []).find(m => m.userId === last.senderId)?.name || '').split(' ')[0];
  const what = last.text || (last.placeId ? '📍 shared a place' : '');
  return who ? `${who}: ${what}` : what;
}

// ── Conversations list (inside the Friends tab) ─────────────────────────────
export function ChatsSection({ conversations, loading, onOpenChat }) {
  const uid = larkleGetUid();
  if (loading && conversations == null) {
    return [1, 2, 3].map(i => (
      <div key={i} style={{
        background: 'var(--paper)', borderRadius: 16, padding: '12px 14px',
        marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: 'var(--bg-tint)', animation: 'larkle-shimmer 1.4s ease-in-out infinite',
        }}/>
        <div className="col" style={{ flex: 1, gap: 6 }}>
          {[['40%', 13, 0], ['70%', 11, 0.12]].map(([w, h, delay], j) => (
            <div key={j} style={{
              width: w, height: h, borderRadius: 6, background: 'var(--bg)',
              animation: `larkle-shimmer 1.4s ease-in-out ${delay}s infinite`,
            }}/>
          ))}
        </div>
      </div>
    ));
  }
  const list = conversations || [];
  if (list.length === 0) {
    return (
      <div style={{ padding: '48px 20px', textAlign: 'center' }}>
        <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8, lineHeight: 1.1 }}>
          No chats yet
        </div>
        <p className="soft" style={{ fontSize: 14 }}>
          Share a place with a friend or start a plan — the conversation shows up here.
        </p>
      </div>
    );
  }
  return list.map(c => (
    <button key={c.id} onClick={() => onOpenChat(c.id)} style={{
      width: '100%', border: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
      background: 'var(--paper)', borderRadius: 16, padding: '12px 14px',
      marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <ThreadAvatar conversation={c} uid={uid}/>
      <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <div className="row between" style={{ gap: 8 }}>
          <span style={{
            fontWeight: c.unread ? 700 : 600, fontSize: 14,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{conversationLabel(c, uid)}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-mute)', flexShrink: 0 }}>
            {c.lastMessage ? timeAgo(c.lastMessage.sentAt) : ''}
          </span>
        </div>
        <span style={{
          fontSize: 12.5, color: c.unread ? 'var(--ink)' : 'var(--ink-mute)',
          fontWeight: c.unread ? 600 : 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{previewText(c, uid)}</span>
      </div>
      {c.unread && <span style={{ width: 9, height: 9, borderRadius: 999, background: 'var(--coral)', flexShrink: 0 }}/>}
    </button>
  ));
}

// ── Start-a-chat panel (friend multi-pick, mirrors AddFriendPanel) ──────────
export function StartChatPanel({ friends, onClose, onStarted, onToast }) {
  const [picked, setPicked] = React.useState([]);
  const [starting, setStarting] = React.useState(false);
  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const start = () => {
    if (picked.length === 0 || starting) return;
    setStarting(true);
    apiStartConversation(picked)
      .then(convo => { onStarted(convo); })
      .catch(err => onToast(err?.offline ? "You're offline" : 'Could not start the chat'))
      .finally(() => setStarting(false));
  };

  return (
    <div style={{
      margin: '0 16px 12px', background: 'var(--paper)', borderRadius: 16,
      padding: '14px 14px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div className="row between" style={{ marginBottom: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13.5 }}>New chat</span>
        <button className="iconbtn" onClick={onClose} aria-label="Close" style={{ width: 28, height: 28 }}>
          <span style={{ width: 13, height: 13 }}>{Icon.x}</span>
        </button>
      </div>
      {friends.length === 0 ? (
        <p className="soft" style={{ fontSize: 13, margin: '0 0 6px' }}>Add some friends first — chats are between friends.</p>
      ) : (
        <>
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10 }}>
            {friends.map(f => (
              <button key={f.userId} onClick={() => toggle(f.userId)} style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                border: 0, background: 'transparent', padding: '6px 0',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <FriendAvatar friend={f} size={32}/>
                <span style={{ flex: 1, textAlign: 'left', fontSize: 13.5, fontWeight: 500 }}>{f.name}</span>
                <div style={{
                  width: 20, height: 20, borderRadius: 999,
                  border: picked.includes(f.userId) ? '6px solid var(--coral)' : '1.5px solid var(--hair)',
                  background: picked.includes(f.userId) ? 'var(--coral)' : 'transparent',
                }}/>
              </button>
            ))}
          </div>
          <button className="btn coral full" style={{ height: 40, fontSize: 13.5, opacity: picked.length === 0 ? 0.5 : 1 }}
                  disabled={picked.length === 0 || starting} onClick={start}>
            {starting ? 'Starting…' : picked.length > 1 ? `Start group chat (${picked.length})` : 'Start chat'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Message thread ───────────────────────────────────────────────────────────
const POLL_MS = 4000;

function PlaceBubbleCard({ place, onOpenPlace }) {
  const activity = React.useMemo(() => placeToActivity(place), [place.id]);
  return (
    <button onClick={() => onOpenPlace(activity)} style={{
      display: 'flex', gap: 10, padding: 8, borderRadius: 14, width: 210,
      background: 'var(--paper)', border: '0.5px solid var(--hair)',
      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
      boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
    }}>
      <div style={{ width: 54, flexShrink: 0 }}>
        <HeroArt activity={activity} height={54} rounded={9} showNav={false}/>
      </div>
      <div className="col" style={{ justifyContent: 'center', minWidth: 0, gap: 2 }}>
        <span className="serif" style={{ fontSize: 14.5, color: 'var(--ink)', lineHeight: 1.1 }}>{activity.name}</span>
        <span className="muted" style={{ fontSize: 10.5 }}>{activity.kind}</span>
      </div>
    </button>
  );
}

export function ChatThreadSheet({ conversationId, onClose, onOpenPlace, onRead, onToast }) {
  const uid = larkleGetUid();
  const [conversation, setConversation] = React.useState(null);
  const [messages, setMessages] = React.useState(null); // ascending; null = loading
  const [draft, setDraft] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const scrollRef = React.useRef(null);
  const stickToBottomRef = React.useRef(true);
  const lastSeenRef = React.useRef(null); // newest server message id we marked read

  // esc-to-close + focus (mirrors useSheetA11y in modals.jsx, local to avoid
  // exporting internals from there)
  const sheetRef = React.useRef(null);
  React.useEffect(() => {
    sheetRef.current?.focus?.({ preventScroll: true });
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const markRead = React.useCallback((newestId) => {
    if (newestId && lastSeenRef.current === newestId) return;
    lastSeenRef.current = newestId;
    apiMarkConversationRead(conversationId).then(() => onRead?.(conversationId)).catch(() => {});
  }, [conversationId, onRead]);

  // Load + poll. Optimistic sends (tmp- ids) survive merges until the server
  // copy shows up.
  React.useEffect(() => {
    let alive = true;
    setConversation(null);
    setMessages(null);
    lastSeenRef.current = null;
    apiGetConversation(conversationId)
      .then(c => { if (alive) setConversation(c); })
      .catch(err => { if (alive) { onToast(err?.offline ? "You're offline" : 'Could not open the chat'); onClose(); } });

    const load = () => {
      apiFetchMessages(conversationId, { limit: 50 })
        .then(list => {
          if (!alive) return;
          const ascending = [...(list || [])].reverse();
          setMessages(prev => {
            const serverIds = new Set(ascending.map(m => m.id));
            const pending = (prev || []).filter(m =>
              String(m.id).startsWith('tmp-') &&
              // keep until an equivalent server message lands
              !ascending.some(s => s.senderId === m.senderId && s.text === m.text && s.sentAt >= m.sentAt - 60_000)
            );
            return [...ascending, ...pending];
          });
          if (ascending.length > 0) markRead(ascending[ascending.length - 1].id);
        })
        .catch(() => {});
    };
    load();
    const iv = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [conversationId]);

  // Pin to bottom while the user hasn't scrolled up
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages?.length, conversation]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const send = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setDraft('');
    stickToBottomRef.current = true;
    const optimistic = {
      id: `tmp-${Date.now()}`, conversationId, senderId: uid,
      kind: 'user', text, sentAt: Date.now(),
    };
    setMessages(m => [...(m || []), optimistic]);
    apiSendMessage(conversationId, { text })
      .then(saved => {
        setMessages(m => (m || []).map(x => x.id === optimistic.id ? { ...saved } : x));
        lastSeenRef.current = saved.id; // own message needs no read receipt
      })
      .catch(err => {
        setMessages(m => (m || []).filter(x => x.id !== optimistic.id));
        setDraft(text);
        onToast(err?.offline ? "You're offline" : 'Could not send');
      })
      .finally(() => setSending(false));
  };

  const memberFor = (id) => (conversation?.members || []).find(m => m.userId === id);
  const label = conversation ? conversationLabel(conversation, uid) : '';
  const isGroup = conversation?.type === 'group';

  // Insert a small time divider when there's a >30 min gap
  const rows = [];
  (messages || []).forEach((m, i) => {
    const prev = (messages || [])[i - 1];
    if (!prev || m.sentAt - prev.sentAt > 30 * 60_000) {
      rows.push({ divider: true, key: `t-${m.id}`, ts: m.sentAt });
    }
    rows.push({ message: m, key: m.id, showName: isGroup && m.senderId !== uid && m.kind !== 'system' && (!prev || prev.senderId !== m.senderId || prev.kind === 'system') });
  });

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-label={label || 'Chat'}
           style={{ maxHeight: '92%', height: '92%' }}>
        <div className="handle"/>
        <div className="row between" style={{ marginBottom: 10 }}>
          <div className="row" style={{ gap: 10, minWidth: 0 }}>
            {conversation && <ThreadAvatar conversation={conversation} uid={uid} size={34}/>}
            <div className="col" style={{ minWidth: 0 }}>
              <h3 className="serif" style={{
                fontSize: 20, margin: 0, color: 'var(--ink)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{label || '…'}</h3>
              {isGroup && (
                <span className="muted" style={{ fontSize: 11 }}>
                  {conversation.members.length} people
                </span>
              )}
            </div>
          </div>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <span style={{ width: 16, height: 16 }}>{Icon.x}</span>
          </button>
        </div>

        <div ref={scrollRef} onScroll={onScroll}
             style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 2px 10px' }}>
          {messages == null ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <span className="soft" style={{ fontSize: 13 }}>Loading…</span>
            </div>
          ) : messages.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 22, color: 'var(--terracotta)', marginBottom: 6 }}>
                {isGroup ? 'The group chat starts here' : 'Say hi'}
              </div>
              <p className="soft" style={{ fontSize: 13, margin: 0 }}>
                Messages and shared places with {label} land in this thread.
              </p>
            </div>
          ) : rows.map(row => {
            if (row.divider) {
              return (
                <div key={row.key} style={{ textAlign: 'center', margin: '14px 0 10px' }}>
                  <span style={{ fontSize: 10.5, color: 'var(--ink-mute)', fontWeight: 600, letterSpacing: '0.04em' }}>
                    {new Date(row.ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' · '}
                    {new Date(row.ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              );
            }
            const m = row.message;
            if (m.kind === 'system') {
              return (
                <div key={row.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, margin: '10px 0' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', textAlign: 'center', maxWidth: 260 }}>{m.text}</span>
                  {m.place && <PlaceBubbleCard place={m.place} onOpenPlace={onOpenPlace}/>}
                </div>
              );
            }
            const mine = m.senderId === uid;
            const sender = memberFor(m.senderId);
            return (
              <div key={row.key} style={{
                display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start',
                alignItems: 'flex-end', gap: 7, margin: '3px 0',
              }}>
                {!mine && isGroup && (
                  <div style={{ width: 24, flexShrink: 0 }}>
                    {row.showName || !rowsHaveNextFromSame(rows, row) ? <FriendAvatar friend={sender || { userId: m.senderId, name: '?' }} size={24}/> : null}
                  </div>
                )}
                <div className="col" style={{ maxWidth: '76%', alignItems: mine ? 'flex-end' : 'flex-start', gap: 3 }}>
                  {row.showName && (
                    <span style={{ fontSize: 10.5, color: 'var(--ink-mute)', fontWeight: 600, margin: '4px 6px 0' }}>
                      {(sender?.name || (sender?.handle && `@${sender.handle}`) || 'Someone').split(' ')[0]}
                    </span>
                  )}
                  {m.place && <PlaceBubbleCard place={m.place} onOpenPlace={onOpenPlace}/>}
                  {m.text && (
                    <div style={{
                      padding: '8px 12px', borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: mine ? 'var(--coral)' : 'var(--bg-tint)',
                      color: mine ? '#fff' : 'var(--ink)',
                      fontSize: 14, lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      opacity: String(m.id).startsWith('tmp-') ? 0.6 : 1,
                      border: mine ? 'none' : '0.5px solid var(--hair)',
                    }}>{m.text}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="row" style={{ gap: 8, paddingTop: 10, borderTop: '0.5px solid var(--hair)' }}>
          <input value={draft} onChange={e => setDraft(e.target.value.slice(0, 2000))}
                 onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                 placeholder={`Message ${label || ''}`}
                 aria-label="Message"
                 style={{
                   flex: 1, height: 40, boxSizing: 'border-box',
                   border: '0.5px solid var(--hair)', borderRadius: 999,
                   padding: '0 16px', fontFamily: 'inherit', fontSize: 14,
                   background: 'var(--bg-tint)', outline: 'none', color: 'var(--ink)',
                 }}/>
          <button className="iconbtn solid" aria-label="Send" disabled={!draft.trim() || sending}
                  onClick={send}
                  style={{ width: 40, height: 40, opacity: draft.trim() ? 1 : 0.4 }}>
            <svg viewBox="0 0 24 24" fill="none" style={{ width: 17, height: 17 }}>
              <path d="M4 12l16-7-5 7 5 7-16-7z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
              <path d="M4 12h11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

// Avatar goes on the LAST bubble of a run (chat-app style): show it when the
// next row is not another message from the same sender.
function rowsHaveNextFromSame(rows, row) {
  const idx = rows.indexOf(row);
  const next = rows[idx + 1];
  return !!(next && next.message && next.message.senderId === row.message.senderId && next.message.kind !== 'system');
}
